import Phaser from "phaser";
import {
  CommandRegistry,
  Runner,
  compile,
  parse,
  type CompiledScript,
  type ExecContext,
  type ScriptLoader,
  type TextSegment,
} from "@pix/script";
import { AssetManager } from "../runtime/assetManager";
import { AudioBackend } from "../audio/audioBackend";
import { PlayerState } from "../runtime/playerState";
import { registerCoreDisplay } from "../plugins/coreDisplay";
import { registerCoreAudio } from "../plugins/coreAudio";
import { loadSave, saveRunner } from "../save/saveStore";

/**
 * メインのゲームシーン。bg/chara/face の表示と Runner の駆動。
 */
export class GameScene extends Phaser.Scene {
  private bgImage!: Phaser.GameObjects.Image;
  private bgRect!: Phaser.GameObjects.Rectangle;
  private bgLabel!: Phaser.GameObjects.Text;
  private charLayer!: Phaser.GameObjects.Container;
  private faceLayer!: Phaser.GameObjects.Container;

  private characters = new Map<
    string,
    {
      sprite: Phaser.GameObjects.Image | null;
      placeholder: Phaser.GameObjects.Rectangle | null;
      label: Phaser.GameObjects.Text;
      x: number;
      y: number;
      expression: string;
    }
  >();
  private faceItem: {
    sprite: Phaser.GameObjects.Image | null;
    placeholder: Phaser.GameObjects.Rectangle | null;
    label: Phaser.GameObjects.Text;
    id: string;
  } | null = null;

  private runner: Runner | null = null;
  private assets!: AssetManager;
  private audio!: AudioBackend;
  private playerState!: PlayerState;
  private mode: "new" | "continue" = "new";
  private currentBg: string | null = null;

  constructor() {
    super("GameScene");
  }

  init(data: { mode?: "new" | "continue" }): void {
    this.mode = data?.mode ?? "new";
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    this.bgRect = this.add.rectangle(w / 2, h / 2, w, h, 0x14141e).setOrigin(0.5);
    this.bgImage = this.add.image(w / 2, h / 2, "_blank").setVisible(false);
    this.bgLabel = this.add
      .text(w / 2, 60, "[bg: -]", {
        fontFamily: "monospace",
        fontSize: "13px",
        color: "#445566",
      })
      .setOrigin(0.5);

    this.charLayer = this.add.container(0, 0);
    this.faceLayer = this.add.container(0, 0);

    this.assets = this.game.registry.get("assets") as AssetManager;
    const manifestId = this.assets.getManifest().id;
    this.playerState = new PlayerState(manifestId);
    this.audio = new AudioBackend(this.assets);
    this.audio.setBgmVolume(this.playerState.config.bgmVolume);
    this.audio.setSeVolume(this.playerState.config.seVolume);
    this.audio.setVoiceVolume(this.playerState.config.voiceVolume);

    this.game.registry.set("audio", this.audio);
    this.game.registry.set("playerState", this.playerState);
    this.game.registry.set("game-scene", this);

    // UIScene をオーバーレイ起動
    this.scene.launch("UIScene");

    this.time.delayedCall(60, () => {
      this.startRunner().catch((err) => {
        console.error("[GameScene] Runner failed:", err);
      });
    });
  }

  getRunner(): Runner | null {
    return this.runner;
  }

  // =================================================================
  // 表示プラグイン用 API
  // =================================================================

  async charaShow(id: string, opts: Record<string, unknown>): Promise<void> {
    if (!id) return;
    const expression = String(opts["face"] ?? opts["expression"] ?? "normal");
    const w = this.scale.width;
    const h = this.scale.height;
    const pos = String(opts["pos"] ?? "center");
    const x = posToX(pos, w);
    const y = h - 220;

    let entry = this.characters.get(id);
    if (entry) {
      this.tweens.add({ targets: [entry.sprite, entry.placeholder, entry.label], x, duration: 250 });
      entry.x = x;
      await this.charaFace(id, expression);
      return;
    }

    const key = this.assets.getCharaSpriteKey(id, expression);
    let sprite: Phaser.GameObjects.Image | null = null;
    let placeholder: Phaser.GameObjects.Rectangle | null = null;
    if (this.textures.exists(key)) {
      sprite = this.add.image(x, y, key).setOrigin(0.5, 0.5);
      const targetH = 460;
      sprite.setScale(targetH / sprite.height);
    } else {
      placeholder = this.add
        .rectangle(x, y, 220, 380, hashColor(id), 0.85)
        .setStrokeStyle(2, 0x111122);
    }
    const label = this.add
      .text(x, y + 200, `${this.assets.getCharaName(id)} / ${expression}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        color: "#444455",
        backgroundColor: "#ffffff88",
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5);
    if (sprite) this.charLayer.add(sprite);
    if (placeholder) this.charLayer.add(placeholder);
    this.charLayer.add(label);
    this.characters.set(id, { sprite, placeholder, label, x, y, expression });
  }

  async charaHide(id: string): Promise<void> {
    const c = this.characters.get(id);
    if (!c) return;
    c.sprite?.destroy();
    c.placeholder?.destroy();
    c.label.destroy();
    this.characters.delete(id);
  }

  async charaHideAll(): Promise<void> {
    for (const [, c] of this.characters) {
      c.sprite?.destroy();
      c.placeholder?.destroy();
      c.label.destroy();
    }
    this.characters.clear();
  }

  async charaFace(id: string, expression: string): Promise<void> {
    const c = this.characters.get(id);
    if (!c) return;
    if (c.expression === expression) return;
    c.expression = expression;
    const key = this.assets.getCharaSpriteKey(id, expression);
    if (c.sprite && this.textures.exists(key)) {
      c.sprite.setTexture(key);
    } else if (c.placeholder) {
      c.placeholder.setFillStyle(hashColor(id + expression));
    }
    c.label.setText(`${this.assets.getCharaName(id)} / ${expression}`);
  }

  async charaMove(id: string, opts: Record<string, unknown>): Promise<void> {
    const c = this.characters.get(id);
    if (!c) return;
    const pos = String(opts["pos"] ?? "center");
    const x = posToX(pos, this.scale.width);
    const time = Number(opts["time"] ?? 0.3) * 1000;
    this.tweens.add({
      targets: [c.sprite, c.placeholder, c.label].filter(Boolean) as Phaser.GameObjects.GameObject[],
      x,
      duration: time,
    });
    c.x = x;
  }

  async faceShow(id: string, expression: string): Promise<void> {
    if (this.faceItem) {
      this.faceItem.sprite?.destroy();
      this.faceItem.placeholder?.destroy();
      this.faceItem.label.destroy();
    }
    const h = this.scale.height;
    const x = 100;
    const y = h - 130;
    const key = this.assets.getFaceKey(id, expression);
    let sprite: Phaser.GameObjects.Image | null = null;
    let placeholder: Phaser.GameObjects.Rectangle | null = null;
    if (this.textures.exists(key)) {
      sprite = this.add.image(x, y, key).setOrigin(0.5);
      const target = 160;
      sprite.setScale(target / Math.max(sprite.width, sprite.height));
    } else {
      placeholder = this.add
        .rectangle(x, y, 160, 160, hashColor(id + expression), 0.95)
        .setStrokeStyle(2, 0xffd28a);
    }
    const label = this.add
      .text(x, y + 90, `${this.assets.getCharaName(id)} / ${expression}`, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px",
        color: "#222233",
        backgroundColor: "#ffd28aaa",
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5);
    if (sprite) this.faceLayer.add(sprite);
    if (placeholder) this.faceLayer.add(placeholder);
    this.faceLayer.add(label);
    this.faceItem = { sprite, placeholder, label, id };
  }

  async faceHide(): Promise<void> {
    if (!this.faceItem) return;
    this.faceItem.sprite?.destroy();
    this.faceItem.placeholder?.destroy();
    this.faceItem.label.destroy();
    this.faceItem = null;
  }

  async faceSetExpression(expression: string): Promise<void> {
    if (!this.faceItem) return;
    const id = this.faceItem.id;
    await this.faceShow(id, expression);
  }

  async windowShow(): Promise<void> {
    /* UIScene 側で常時表示中 */
  }
  async windowHide(): Promise<void> {
    /* MVP では実装しない */
  }

  async screenEffect(action: string, opts: Record<string, unknown>): Promise<void> {
    if (action === "black" || action === "white") {
      const inMode = opts["in"] !== undefined;
      const fade = Number(opts["in"] ?? opts["out"] ?? 30) * (1000 / 60);
      const c = action === "white" ? 0xffffff : 0x000000;
      const overlay = this.add
        .rectangle(
          this.scale.width / 2,
          this.scale.height / 2,
          this.scale.width,
          this.scale.height,
          c,
          inMode ? 0 : 1,
        )
        .setDepth(50);
      this.tweens.add({
        targets: overlay,
        alpha: inMode ? 1 : 0,
        duration: fade,
        onComplete: () => overlay.destroy(),
      });
    } else if (action === "shake") {
      const time = Number(opts["time"] ?? 20) * (1000 / 60);
      const power = Number(opts["power"] ?? 5);
      this.cameras.main.shake(time, power / 100);
    } else if (action === "flash") {
      const time = Number(opts["time"] ?? 30) * (1000 / 60);
      this.cameras.main.flash(time, 255, 255, 255);
    }
  }

  async transition(_opts: Record<string, unknown>): Promise<void> {
    /* MVP では未実装 */
  }

  // =================================================================
  // 背景
  // =================================================================

  async setBackground(name: string | null, opts?: Record<string, unknown>): Promise<void> {
    if (name === null) {
      this.bgImage.setVisible(false);
      this.bgRect.setFillStyle(0x14141e);
      this.bgLabel.setText("[bg: -]");
      this.currentBg = null;
      return;
    }
    const fadeSec = Number(opts?.["fade"] ?? 0);
    const key = this.assets.getBgKey(name);
    if (this.textures.exists(key)) {
      this.bgRect.setVisible(false);
      this.bgImage.setTexture(key);
      const targetSx = this.scale.width / this.bgImage.width;
      const targetSy = this.scale.height / this.bgImage.height;
      this.bgImage.setScale(Math.max(targetSx, targetSy));
      if (fadeSec > 0) {
        this.bgImage.setAlpha(0).setVisible(true);
        this.tweens.add({ targets: this.bgImage, alpha: 1, duration: fadeSec * 1000 });
      } else {
        this.bgImage.setAlpha(1).setVisible(true);
      }
    } else {
      this.bgImage.setVisible(false);
      this.bgRect.setVisible(true);
      this.bgRect.setFillStyle(hashColor(name));
    }
    this.bgLabel.setText(`[bg: ${name}]`);
    this.currentBg = name;
  }

  getCurrentBg(): string | null {
    return this.currentBg;
  }

  // =================================================================
  // ランナーの起動
  // =================================================================

  private async startRunner(): Promise<void> {
    const manifest = this.assets.getManifest();
    const [entryFile, entryLabel] = manifest.entry.split("#");
    if (!entryFile || !entryLabel) throw new Error(`不正な entry: ${manifest.entry}`);

    const ui = this.scene.get("UIScene");
    const uiBackend: ExecContext["ui"] = {
      showMessage: (s, t: TextSegment[], pos) =>
        (ui as unknown as { showMessage: ExecContext["ui"]["showMessage"] }).showMessage(s, t, pos),
      showChoice: (opts) =>
        (ui as unknown as { showChoice: ExecContext["ui"]["showChoice"] }).showChoice(opts),
    };

    const registry = new CommandRegistry();
    registerCoreDisplay(registry, this);
    registerCoreAudio(registry);

    const inMemoryScripts = this.game.registry.get("inMemoryScripts") as
      | Record<string, string>
      | undefined;
    const baseUrl = (this.game.registry.get("baseUrl") as string | undefined) ?? "./sample";
    const loader: ScriptLoader = {
      load: async (filename: string): Promise<CompiledScript> => {
        let src: string | undefined;
        if (inMemoryScripts) {
          src = inMemoryScripts[`${filename}.pix`] ?? inMemoryScripts[filename];
        }
        if (src === undefined) {
          const res = await fetch(`${baseUrl}/${filename}.pix`);
          if (!res.ok) throw new Error(`${filename}.pix が読めません: ${res.status}`);
          src = await res.text();
        }
        return compile(parse(src, filename));
      },
    };

    const audio = this.audio;
    const ctx: ExecContext = {
      ui: uiBackend,
      display: {
        setBackground: async (name, opts) => this.setBackground(name, opts),
        showCharacter: async (id, opts) => this.charaShow(id, opts ?? {}),
        hideCharacter: async (id) =>
          id === "*" ? this.charaHideAll() : this.charaHide(id),
        setCharacterFace: async (id, expr) => this.charaFace(id, expr),
        showFace: async (id, expr) => this.faceShow(id, expr),
        hideFace: async () => this.faceHide(),
        setFaceExpression: async (expr) => this.faceSetExpression(expr),
        screen: async (action, opts) => this.screenEffect(action, opts ?? {}),
      },
      audio: {
        playBgm: async (file, opts) => audio.playBgm(file, opts),
        stopBgm: async (opts) => audio.stopBgm(opts),
        playSe: async (slot, file, opts) => audio.playSe(slot, file, opts),
        stopSe: async (slot) => audio.stopSe(slot),
        playVoice: async (ch, file, opts) => audio.playVoice(ch, file, opts),
        stopVoice: async (ch) => audio.stopVoice(ch),
      },
      vars: {
        serialize: () => this.runner?.vars.serialize() ?? {},
      },
      wait: {
        frame: (n) => sleep((n * 1000) / 60),
        time: (ms) => sleep(ms),
        key: () => uiBackend.showMessage(null, [{ kind: "text", value: "..." }], undefined),
      },
      save: {
        save: async (slot) => {
          if (this.runner) saveRunner(this.runner, manifest.id, String(slot));
        },
        load: async (slot) => {
          if (!this.runner) return;
          const data = loadSave(manifest.id, String(slot));
          if (data) await this.runner.setState(data.state);
        },
      },
      navigation: {
        navigate: async () => {
          /* Runner 内で処理される */
        },
      },
    };

    const initial = await loader.load(entryFile);
    const runner = new Runner(initial, { registry, ctx, loader });
    this.runner = runner;

    // モード判定: new → 先頭、continue → autoセーブをロード
    if (this.mode === "continue") {
      const save = loadSave(manifest.id, "auto");
      if (save) {
        await runner.setState(save.state);
      } else {
        runner.jumpToLabel(entryLabel);
      }
    } else {
      runner.jumpToLabel(entryLabel);
    }

    try {
      await runner.run();
    } catch (err) {
      console.error("[Runner] エラー:", err);
    }
  }
}

// =================================================================
// ヘルパー
// =================================================================

function posToX(pos: string, w: number): number {
  switch (pos) {
    case "left":
      return w * 0.25;
    case "right":
      return w * 0.75;
    case "center":
    default:
      return w * 0.5;
  }
}

function hashColor(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return hsvToRgbHex(hue, 0.5, 0.7);
}

function hsvToRgbHex(h: number, s: number, v: number): number {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const R = Math.round((r + m) * 255);
  const G = Math.round((g + m) * 255);
  const B = Math.round((b + m) * 255);
  return (R << 16) | (G << 8) | B;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
